import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Paper,
  ThemeIcon,
  Stack,
  Box,
  Group,
  useComputedColorScheme,
} from "@mantine/core";
import { IconShieldCheck, IconLock, IconKey } from "@tabler/icons-react";
import { motion } from "framer-motion";
import { AnimateIn, staggerContainer, fadeUpItem } from "./AnimateIn";

const badges = [
  {
    icon: IconShieldCheck,
    title: "HIPAA Ready",
    description:
      "Built for healthcare compliance with PHI protection and audit trails",
    color: "#12b886",
  },
  {
    icon: IconLock,
    // "at rest" is a launch commitment, not a description of preprod: today
    // `kms_key_id` is null in rds/config/{prod,preprod}.yaml. Prod does not
    // exist yet and will be created with the KMS key wired in, so this is
    // true for every real customer's data. If prod is ever created WITHOUT
    // it, this claim must be re-scoped to transit-only.
    title: "Bank-Level Encryption",
    description: "AES-256 encryption at rest and TLS 1.3 for data in transit",
    color: "#4c6ef5",
  },
  {
    icon: IconKey,
    title: "Role-Based Access Control",
    description:
      "Granular per-facility permissions, with MFA required on admin access",
    color: "#15aabf",
  },
];

export function SecurityBadgesSection() {
  const computedColorScheme = useComputedColorScheme("light");

  return (
    <Box
      component="section"
      id="security"
      py={80}
      bg={computedColorScheme === "dark" ? "#1A1B1E" : "white"}
    >
      <Container size="xl">
        <AnimateIn>
          <Stack align="center" gap="md" mb={60}>
            <Title order={2} size="2rem" fw={700} ta="center">
              Enterprise-Grade{" "}
              <Text
                component="span"
                inherit
                style={{
                  background:
                    "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Security & Compliance
              </Text>
            </Title>
            <Text size="md" c="dimmed" ta="center" maw={700}>
              Your residents' data deserves the highest level of protection. We
              take security seriously.
            </Text>
          </Stack>
        </AnimateIn>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {badges.map((badge) => {
              const Icon = badge.icon;
              return (
                <motion.div key={badge.title} variants={fadeUpItem}>
                  <Paper
                    p="lg"
                    radius="md"
                    style={{
                      height: "100%",
                      background:
                        computedColorScheme === "dark"
                          ? "linear-gradient(135deg, #25262B 0%, #2C2E33 100%)"
                          : "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                      border:
                        computedColorScheme === "dark"
                          ? "1px solid #373A40"
                          : "1px solid #e9ecef",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 16px rgba(0, 0, 0, 0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    <Group gap="md" align="flex-start">
                      <ThemeIcon
                        size={50}
                        radius="md"
                        variant="light"
                        style={{
                          backgroundColor: `${badge.color}15`,
                          color: badge.color,
                          flexShrink: 0,
                        }}
                      >
                        <Icon size={26} stroke={1.5} />
                      </ThemeIcon>
                      <Stack gap={4} style={{ flex: 1 }}>
                        <Text size="sm" fw={700}>
                          {badge.title}
                        </Text>
                        <Text size="xs" c="dimmed" style={{ lineHeight: 1.5 }}>
                          {badge.description}
                        </Text>
                      </Stack>
                    </Group>
                  </Paper>
                </motion.div>
              );
            })}
          </SimpleGrid>
        </motion.div>

        <AnimateIn delay={0.2}>
          <Box
            mt={60}
            p="xl"
            style={{
              borderRadius: "12px",
              background:
                computedColorScheme === "dark"
                  ? "linear-gradient(135deg, #2C2E33 0%, #25262B 100%)"
                  : "linear-gradient(135deg, #e6fcf5 0%, #e8f4ff 100%)",
              border:
                computedColorScheme === "dark"
                  ? "1px solid #373A40"
                  : "1px solid #12b88620",
            }}
          >
            <Stack align="center" gap="md">
              <Text size="lg" fw={600} ta="center">
                Your Data, Your Control
              </Text>
              <Text
                size="sm"
                c="dimmed"
                ta="center"
                maw={800}
                style={{ lineHeight: 1.6 }}
              >
                We implement role-based access control, comprehensive audit
                logging, automatic backups, and data isolation to ensure your
                information stays secure. All staff actions are logged, and you
                maintain complete ownership of your data with export
                capabilities at any time.
              </Text>
            </Stack>
          </Box>
        </AnimateIn>
      </Container>
    </Box>
  );
}
